import React, { useEffect, useRef, useState } from 'react'
import { Link, useHistory, useLocation, useParams } from 'react-router-dom'
import { LeaveGuard } from '../../../../shared/sso-workspace/leave-guard'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { DockedSaveBar } from '../../../../shared/sso-workspace/save-bar'
import { formatAgo, formatDateTime, plural, serverMessage } from '../../../../shared/sso-workspace/format'
import { Menu, MenuItem } from '../../../../shared/menu/menu'
import { IconAdd, IconDelete } from '../../../../shared/icon/icon'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'
import { useAuditRecord } from '../../../../shared/audit/history/audit-record-context'
import { useAlertRoutingAccess } from './access'
import { useCatalog } from './catalog'
import { SchemaForm, validateSchema } from './schema-form'
import { splitConfig } from './editor-steps'
import { VerifySnippet } from './verify-snippet'
import { SecretPanel } from './secret-panel'
import { addAddress, createTarget, getTarget, listRoutes, removeAddress, setPersonal, setSecret, updateTarget } from './alert-routing-api'
import { NEW_TARGET, TARGETS_PAGE, newTargetHref, routeHref, runsForTarget, targetHref } from './paths'
import { TargetTypeStep } from './target-type-step'
import { TestOutcomeLine, useTargetActions } from './target-actions'
import { statusOf } from './connection-field'
import {
  PERSONAL_HELP, PERSONAL_WARNING, deleteBlocked, isFailing, personalTile, takesPersonalSwitch, targetStatus, targetSubtitle, typeHelp, typeNoun, typeTitle, whereOf
} from './target-words'
import { targetSetup } from './setup-words'
import { triggerSummary } from './route-summary'
import { AddToRouteDialog } from './add-to-route-dialog'
import {
  AlertPlainPage, AlertWorkspace, ArSection, EmptyLine, LinkRows, SetupNotice, SetupPill, TypeLogo, When, runItem, useRecentRuns
} from './workspace-parts'

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
export const CREATED_NOTE = 'Created. Add it to a route, then send a test to check it.'

// A config value as the form compares it: a field never touched and an empty
// one are the same.
const norm = v => (v === undefined || v === null ? '' : typeof v === 'object' ? JSON.stringify(v) : String(v))

// What is unsaved: the name, each plain config field, the personal data switch.
export const changesOf = (saved, edit, schema) => {
  if (!saved) return 0
  const plain = schema.filter(f => !f.secret && f.type !== 'email_list')
  return (edit.name.trim() !== (saved.name || '') ? 1 : 0) +
    plain.filter(f => norm(edit.config[f.key]) !== norm((saved.config || {})[f.key])).length +
    (Boolean(edit.personal) !== Boolean(saved.allowPersonal) ? 1 : 0)
}

// The secrets section's title by type: a webhook's URL and signing secret, a
// Slack incoming webhook's link.
const SECRETS_TITLE = { webhook: 'Secrets and signature check', slack: 'Webhook link' }
const SECRETS_LEDE = {
  webhook: 'A saved secret can be replaced, never read.',
  slack: 'The link Slack gave you. It can be replaced, never read.'
}

// The sections a saved target has, by type (SectionNav).
export const targetSections = (target, at) => {
  const capabilities = (at && at.capabilities) || {}
  const secrets = ((at && at.configSchema) || []).some(f => f.secret)
  return [
    { id: 'details', label: 'Details' },
    capabilities.perAddress && { id: 'addresses', label: 'Addresses' },
    secrets && { id: 'secrets', label: target.type === 'slack' ? 'Webhook link' : 'Secrets' },
    { id: 'routes', label: 'Routes' },
    { id: 'deliveries', label: 'Deliveries' }
  ].filter(Boolean)
}

// The summary strip (status-strip.js tiles): where it sends, the routes that
// use it, its last delivery, personal data and its rate.
export const targetTiles = (t, at, routes) => {
  const usedBy = t.usedBy || 0
  const failing = isFailing(t)
  const sendsTo = t.type === 'email'
    ? (t.addresses || []).length
      ? { value: plural(t.addresses.length, 'address', 'addresses'), detail: (t.config || {}).email_mode === 'together' ? 'one email to everyone' : 'one email per address', section: 'addresses' }
      : { value: 'No addresses', detail: 'add one under Addresses', tone: 'warn', dot: 'warn', section: 'addresses' }
    : t.type === 'slack_app'
      ? { value: whereOf(t) || 'No channel', detail: (t.connection && t.connection.externalName) || 'no workspace', section: 'details', nowrap: true }
      : { value: t.host || (Object.values(t.secrets || {}).some(s => s && s.set) ? 'Set up' : 'Not set up'), detail: typeTitle(at) || t.type, section: 'details', nowrap: Boolean(t.host) }
  const names = (routes || []).map(r => r.name)
  const personal = personalTile(t)
  return [
    { key: 'sends', label: 'sends to', ...sendsTo },
    {
      key: 'routes', label: 'routes', value: String(usedBy), section: 'routes', tone: usedBy ? null : 'warn', dot: usedBy ? 'ok' : 'warn',
      detail: usedBy ? (names.length > 1 ? `${names[0]} and ${names.length - 1} more` : names[0] || plural(usedBy, 'route')) : 'only tests reach it'
    },
    {
      key: 'delivery', label: 'last delivery', value: t.lastSuccessAt ? formatAgo(t.lastSuccessAt) : 'Never', section: 'deliveries',
      tone: failing ? 'bad' : null, dot: failing ? 'bad' : null,
      detail: failing ? `failed ${formatAgo(t.lastFailureAt)}` : t.lastSuccessAt ? formatDateTime(t.lastSuccessAt) : 'nothing sent yet'
    },
    { key: 'personal', label: 'personal data', value: personal.value, detail: personal.detail, section: 'details' },
    { key: 'rate', label: 'rate limit', value: t.ratePerMinute ? `${t.ratePerMinute} a minute` : 'None', detail: 'more wait their turn', section: 'details' }
  ]
}

// The routes that send here: an action names the target.
export const routesUsing = (routes, targetID) => (routes || []).filter(r => (r.actions || []).some(a => a.targetID === targetID))

// /admin/alert-routing/targets/:targetID: one target as a page, in the SSO
// connection workspace's anatomy (workspace-parts.js). The header: the type's
// icon, the name, "Slack · #channel · workspace", its status pill, Send test
// as the primary action and Delete last in the ⋮ (held, with why, while a
// route uses it; the same actions as its row, target-actions.js). The strip:
// sends to, routes, last delivery, personal data, rate limit. Then Details
// (the form, Allow personal data with it), Addresses (email) or the secrets,
// Routes (each opening its route, with + Add to a route) and Deliveries (each
// opening the alert, with View all in Alert runs). Saving is the docked save
// bar. History is the target's (alert_target), from the History button.
//
// /targets/new asks what kind first (?type= goes straight to its form;
// &connection= picks the workspace: a connection page's + New Slack target),
// then Create target on the docked bar opens the new target's page.
//
// The page's state belongs to one target: moving to another remounts it, so a
// shown secret, a test or an error never carries across. The one exception is
// create: the address moves from /new to the new id and the secret panel
// must stay.
export const TargetWorkspace = () => {
  const { targetID } = useParams()
  const { search } = useLocation()
  const created = useRef(null)
  const key = useRef(targetID)
  const id = targetID === 'new' ? `new:${new URLSearchParams(search).get('type') || ''}` : targetID
  if (created.current === null || String(created.current) !== targetID) {
    created.current = null
    key.current = id
  }
  return <TargetWorkspaceBody key={key.current} onCreated={newID => { created.current = newID }} />
}

const TargetWorkspaceBody = ({ onCreated }) => {
  const { targetID } = useParams()
  const isNew = targetID === 'new'
  const location = useLocation()
  const query = new URLSearchParams(location.search)
  const type = query.get('type') || ''
  const history = useHistory()
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  // Back from a consent page (connect-return.js): the target as it was
  // being filled in (its workspace picked), what happened, and what didn't.
  const [arrived] = useState(() => location.state || {})
  const draft = arrived.draft || null
  const preset = query.get('connection')
  const blank = { type, name: '', config: {}, secrets: {}, addresses: [], allowPersonal: false }
  const [t, setT] = useState(isNew ? blank : null)
  const [name, setName] = useState(isNew && draft ? draft.name || '' : '')
  const [form, setForm] = useState(isNew ? (draft ? draft.config || {} : preset ? { connection_id: Number(preset) } : {}) : {})
  const [personal, setPersonalOn] = useState(false)
  // History: whose target this is, once loaded (null: a platform target),
  // for a reader without SYS_AUDIT_LOG (record-routes.js recordOrg)
  const loaded = !isNew && Boolean(t)
  useAuditRecord({ type: 'alert_target', id: loaded ? targetID : undefined, organizationID: loaded ? (t.organizationID ?? null) : undefined })
  const [errors, setErrors] = useState({})
  const [shown, setShown] = useState(null)
  const [address, setAddress] = useState('')
  const [addressError, setAddressError] = useState(null)
  const [status, setStatus] = useState('')
  const [error, failure, setError] = useErrorLine()
  const [busy, setBusy] = useState(false)
  const [routes, setRoutes] = useState(undefined)
  const [routesKey, setRoutesKey] = useState(0)
  const [runsKey, setRunsKey] = useState(0)
  const [adding, setAdding] = useState(false)
  // The target just created: the address moves to its id, and there is no
  // need to fetch what the server has just returned.
  const created = useRef(null)
  const actions = useTargetActions({ onDeleted: () => history.push(TARGETS_PAGE) })

  // The saved target becomes the form's starting point.
  const adopt = (v, over) => {
    setT(v)
    setName(over && over.name !== undefined ? over.name : v.name || '')
    setForm(over && over.config ? { ...(v.config || {}), ...over.config } : v.config || {})
    setPersonalOn(Boolean(v.allowPersonal))
  }

  useEffect(() => {
    if (isNew || String(created.current) === targetID) return
    getTarget(targetID).then(v => adopt(v, draft ? { name: draft.name || v.name, config: draft.config || {} } : null),
      err => setError(serverMessage(err, "This target couldn't be loaded."), err))
  }, [targetID, isNew, setError, draft]) // eslint-disable-line react-hooks/exhaustive-deps

  // Every route (the ones that send here, and the ones "Add to a route" offers).
  useEffect(() => {
    if (isNew) return undefined
    let live = true
    listRoutes().then(v => live && setRoutes(v || []), () => live && setRoutes(null))
    return () => { live = false }
  }, [isNew, routesKey])

  const runs = useRecentRuns(!isNew && t && t.id ? [t.id] : undefined, runsKey)

  // What came back is shown here once: the address keeps none of it, so a
  // reload or Back never restores it again.
  useEffect(() => {
    if (location.state && (location.state.draft || location.state.flash || location.state.problem)) {
      history.replace({ pathname: location.pathname, search: location.search })
    }
  }, [location, history])

  const close = () => history.push(TARGETS_PAGE)

  // A new target with no type yet: what kind?
  if (isNew && !type) {
    return (
      <AlertPlainPage list="targets" crumb="New target" title="New target" subtitle="Then add it to a route, so alerts reach it."
        logo={<TypeLogo />}>
        {canEdit
          ? <TargetTypeStep types={catalog.actionTypes} onPick={picked => history.push(newTargetHref(picked))} />
          : <Notice tone="warn">{reason}</Notice>}
      </AlertPlainPage>
    )
  }
  if (isNew && !canEdit) {
    return <AlertPlainPage list="targets" crumb="New target" title="New target" logo={<TypeLogo />}><Notice tone="warn">{reason}</Notice></AlertPlainPage>
  }
  if (!t) {
    return (
      <AlertPlainPage list="targets" crumb="Target" title={error ? 'Target not found' : 'Loading…'} logo={<TypeLogo />}>
        {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      </AlertPlainPage>
    )
  }

  const at = catalog.actionTypes.find(a => a.type === t.type) || { configSchema: [], capabilities: {} }
  const capabilities = at.capabilities || {}
  const secretFields = at.configSchema.filter(f => f.secret)
  const fieldLabel = key => (at.configSchema.find(f => f.key === key) || { label: key }).label
  const usedBy = t.usedBy || 0
  const switchable = takesPersonalSwitch(t.type)
  const unsaved = isNew ? 0 : changesOf(t, { name, config: form, personal }, at.configSchema)
  const run = async fn => {
    if (busy) return
    setBusy(true)
    try { setError(null); setStatus(''); await fn() } catch (err) { setError(serverMessage(err, "That didn't work."), err) } finally { setBusy(false) }
  }

  const save = () => run(async () => {
    const e = validateSchema(at.configSchema, form, isNew)
    if (!name.trim()) e.name = 'Give the target a name.'
    setErrors(e)
    if (Object.keys(e).length) return
    if (isNew) {
      const out = await createTarget({ type: t.type, name: name.trim(), ...splitConfig(at.configSchema, form) })
      created.current = out.target.id
      onCreated(out.target.id)
      let target = out.target
      // Personal data has its own write (PUT /personal-data, its own audit
      // event): made right after the create, inside the same save.
      let refused = null
      if (switchable && personal) {
        try { target = await setPersonal(target.id, true) } catch (err) { refused = err }
      }
      adopt(target)
      setRoutesKey(n => n + 1)
      if (refused) {
        setPersonalOn(true)
        setError(`Created, but personal data wasn't allowed: ${serverMessage(refused, "that didn't work")}. Save to try again.`, refused)
      } else {
        setStatus(CREATED_NOTE)
      }
      if (out.generatedSecrets && Object.keys(out.generatedSecrets).length) setShown({ secrets: out.generatedSecrets })
      history.replace(targetHref(target.id))
      return
    }
    let target = t
    const plainChanged = changesOf(t, { name, config: form, personal: t.allowPersonal }, at.configSchema) > 0
    if (plainChanged) target = await updateTarget(t.id, { name: name.trim(), config: form })
    if (switchable && Boolean(personal) !== Boolean(t.allowPersonal)) {
      try {
        target = await setPersonal(t.id, personal)
      } catch (err) {
        adopt(target, { config: form })
        setPersonalOn(personal)
        throw err
      }
    }
    adopt(target)
    setStatus('Saved.')
  })

  const discard = () => {
    if (isNew) return close()
    adopt(t)
    setErrors({})
    setError(null)
  }

  const add = () => {
    const value = address.trim()
    if (!EMAIL.test(value)) { setAddressError('Enter an email address, like name@example.org.'); return }
    setAddressError(null)
    run(async () => { setT({ ...(await addAddress(t.id, { address: value, label: '' })) }); setAddress('') })
  }

  const test = async () => {
    setStatus('')
    await actions.sendTest({ ...t, config: { ...(t.config || {}) } })
    setRunsKey(n => n + 1)
  }
  const added = route => {
    setAdding(false)
    setStatus(`Added to ${route.name}.`)
    setRoutesKey(n => n + 1)
    getTarget(t.id).then(v => setT(prev => ({ ...prev, usedBy: v.usedBy })), () => {})
  }

  const outcome = actions.outcome && actions.outcome.targetID === t.id ? actions.outcome : null
  const testNotice = (actions.testing || outcome) &&
    <Notice tone={actions.testing ? 'info' : outcome.tone === 'ok' ? 'ok' : outcome.tone === 'bad' ? 'bad' : 'warn'} role="status">
      <TestOutcomeLine outcome={outcome} testing={Boolean(actions.testing)} />
    </Notice>

  const details = (
    <fieldset disabled={!canEdit} className="ar-form ar-details ar-readonly">
      <div className="sw-field">
        <label className="sw-label ar-required" htmlFor="ar-target-name">Name</label>
        <input id="ar-target-name" className={`form-control${errors.name ? ' is-invalid' : ''}`} value={name} onChange={e => setName(e.target.value)} />
        {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
      </div>
      <SchemaForm schema={at.configSchema} value={form} secretsSet={Object.fromEntries(Object.entries(t.secrets || {}).map(([k, v]) => [k, Boolean(v && v.set)]))}
        onChange={setForm} isNew={isNew} errors={errors} hideSecrets={!isNew}
        connect={{ editable: canEdit, ready: at.ready !== false, returnTo: `${location.pathname}${location.search}`, draftName: name }} />
      {switchable &&
        <div className="sw-field">
          <div className="form-check">
            <input id="ar-personal" className="form-check-input" type="checkbox" checked={personal} aria-describedby="ar-personal-help"
              onChange={e => setPersonalOn(e.target.checked)} />
            <label className="form-check-label" htmlFor="ar-personal">Allow personal data</label>
          </div>
          <div className="sw-hint" id="ar-personal-help">{PERSONAL_HELP}</div>
          {personal && !(t.allowPersonal) && <div className="ar-field-warn" role="note">{PERSONAL_WARNING}</div>}
        </div>}
      {isNew && capabilities.perAddress && <p className="sw-hint m-0">You add the addresses once it's created.</p>}
    </fieldset>
  )

  const saveBar = canEdit &&
    <DockedSaveBar testId="target-savebar" on={isNew || unsaved > 0} count={unsaved}
      status={isNew ? <span className="sw-hint">It's created when you save.</span> : undefined} busy={busy} onSave={save} onDiscard={discard}
      saveLabel={isNew ? 'Create target' : 'Save'} busyLabel={isNew ? 'Creating…' : 'Saving…'} discardLabel={isNew ? 'Cancel' : 'Discard'} />

  const leaveGuards =
    <>
      <LeaveGuard when={Boolean(shown)} label="secret not copied" title="Copy the secret first"
        message="The secret won't be shown again. Leave without copying it?" leaveLabel="Leave and lose the secret" />
      <LeaveGuard when={!shown && unsaved > 0} message="Leave this target without saving them?" />
    </>

  // A new target of a known type: its form, Create target on the docked bar.
  if (isNew) {
    return (
      <AlertPlainPage list="targets" crumb="New target" title={`New ${typeNoun(at) || t.type} target`} logo={<TypeLogo type={t.type} />}
        subtitle={<>{typeHelp(at)}{!draft && <> · <button type="button" className="btn btn-link p-0 ar-back" onClick={() => history.push(NEW_TARGET)}>Change the type</button></>}</>}>
        {leaveGuards}
        <div className="ar-notices">
          {arrived.flash && <Notice tone="ok" role="status">{arrived.flash}</Notice>}
          {arrived.problem && <Notice tone="warn">{arrived.problem}</Notice>}
          {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} compact /></Notice>}
        </div>
        <ArSection id="details" title="Details" lede="Its name and where it sends. You add it to a route once it's created.">{details}</ArSection>
        {saveBar}
      </AlertPlainPage>
    )
  }

  const using = routesUsing(routes, t.id)
  const setup = targetSetup(t)
  const deleteWhy = shown ? 'Copy the secret first.' : deleteBlocked(usedBy)
  const state = targetStatus(t, at, statusOf)
  const headerActions = canEdit &&
    <>
      <button type="button" className="btn sw-btn-primary" disabled={Boolean(actions.testing) || unsaved > 0}
        title={unsaved > 0 ? 'Save your changes first.' : undefined} onClick={test}>{actions.testing ? 'Sending…' : 'Send test'}</button>
      <Menu sentenceCase forceDropdown>
        <MenuItem Icon={IconDelete} text="Delete" danger pin={false} show disabled={Boolean(deleteWhy)} title={deleteWhy || undefined} onClick={() => actions.askDelete(t)} />
      </Menu>
    </>
  const addToRoute = canEdit &&
    <button type="button" className="btn sw-btn btn-sm" disabled={!routes} onClick={() => setAdding(true)}><IconAdd /> Add to a route</button>

  return (
    <AlertWorkspace list="targets" crumb={t.name}
      title={t.name} subtitle={targetSubtitle(t, at)} logo={<TypeLogo type={t.type} />}
      status={{ label: state.label, tone: state.tone, reason: state.reason, section: state.section }}
      badges={setup ? <SetupPill /> : null}
      headerActions={headerActions}
      tiles={targetTiles(t, at, using)} sections={targetSections(t, at)}>
      {leaveGuards}
      <div className="ar-notices">
        {arrived.flash && <Notice tone="ok" role="status">{arrived.flash}</Notice>}
        {arrived.problem && <Notice tone="warn">{arrived.problem}</Notice>}
        {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
        {status && <Notice tone="ok" role="status">{status}</Notice>}
        {testNotice}
        {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} compact /></Notice>}
        {shown && <SecretPanel secrets={shown.secrets} labelOf={fieldLabel} rotated={shown.rotated} onConfirm={() => setShown(null)} />}
        {setup && <SetupNotice setup={setup} onAction={canEdit ? () => setAdding(true) : null} disabled={!routes} />}
      </div>

      <ArSection id="details" title="Details" lede="Its name, where it sends and what its alerts may include.">{details}</ArSection>

      {capabilities.perAddress &&
        <ArSection id="addresses" title="Addresses" lede="Who gets these alerts by email.">
          {t.addresses.length === 0
            ? <EmptyLine>No addresses yet: alerts sent here reach nobody.</EmptyLine>
            : <ul className="ar-address-rows">{t.addresses.map(a => (
              <li key={a.id}>
                <span className="ar-address">
                  <span className="ar-address-value">{a.address}</span>
                  {a.label && <span className="sw-cell-sub">{a.label}</span>}
                </span>
                {canEdit && <button type="button" className="btn btn-sm sw-btn-outline-danger" disabled={busy} aria-label={`Remove ${a.address}`}
                  onClick={() => run(async () => setT(await removeAddress(t.id, a.id)))}>Remove</button>}
              </li>))}</ul>}
          {canEdit &&
            <div className="ar-address-add">
              <label className="visually-hidden" htmlFor="ar-addr">Add an address</label>
              <input id="ar-addr" className={`form-control${addressError ? ' is-invalid' : ''}`} type="email" placeholder="name@example.org" value={address}
                onChange={e => setAddress(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add() } }} />
              <button type="button" className="btn sw-btn" disabled={busy} onClick={add}>Add address</button>
              {addressError && <div className="invalid-feedback d-block">{addressError}</div>}
            </div>}
        </ArSection>}

      {secretFields.length > 0 &&
        <ArSection id="secrets" title={SECRETS_TITLE[t.type] || 'Secrets'} lede={SECRETS_LEDE[t.type] || 'Saved secrets can be replaced, never read.'}>
          <div className="ar-secret-rows">
            {secretFields.map(f => (
              <div key={f.key} className="ar-secret-row">
                <div className="ar-secret-name">
                  <span className="sw-label">{f.label}</span>
                  <span className="sw-hint">{((t.secrets || {})[f.key] || {}).set
                    ? <>Set{((t.secrets || {})[f.key] || {}).updatedAt ? <> · changed <When at={t.secrets[f.key].updatedAt} /></> : null}</>
                    : 'Not set'}</span>
                </div>
                {canEdit && (f.generate
                  ? <button type="button" className="btn sw-btn btn-sm" aria-label={`Rotate ${f.label.toLowerCase()}`} disabled={busy || Boolean(shown)} onClick={() => run(async () => {
                    const r = await setSecret(t.id, { field: f.key, rotate: true })
                    setShown({ secrets: { [f.key]: r.secret }, rotated: true })
                  })}>Rotate</button>
                  : <SecretReplace field={f} busy={busy} onSave={secret => run(async () => { await setSecret(t.id, { field: f.key, secret }); setT(await getTarget(t.id)) })} />)}
              </div>
            ))}
          </div>
          {t.type === 'webhook' && <VerifySnippet />}
        </ArSection>}

      <ArSection id="routes" title="Routes" lede="The routes that send alerts here." actions={addToRoute}>
        {routes === undefined && <EmptyLine>Loading…</EmptyLine>}
        {routes === null && <EmptyLine>The routes couldn't be loaded.</EmptyLine>}
        {routes && using.length === 0 && <EmptyLine>No route sends here yet.</EmptyLine>}
        {routes && using.length > 0 &&
          <LinkRows label="Routes" items={using.map(r => {
            const action = (r.actions || []).find(a => a.targetID === t.id)
            return {
              key: r.id,
              to: routeHref(r.id),
              tone: !r.enabled || (action && action.enabled === false) ? null : r.paused ? 'warn' : 'ok',
              title: r.name,
              sub: triggerSummary(r, catalog),
              meta: <>
                {!r.enabled && <Pill>Off</Pill>}
                {r.enabled && r.paused && <Pill tone="warn">Paused</Pill>}
                {action && action.enabled === false && <Pill>Off for this target</Pill>}
              </>,
              when: r.lastMatchedAt ? <>Matched <When at={r.lastMatchedAt} /></> : 'Never matched'
            }
          })} />}
      </ArSection>

      <ArSection id="deliveries" title="Deliveries" lede="The latest alerts sent here."
        actions={<Link className="btn sw-btn btn-sm" to={runsForTarget(t.id)}>View all in Alert runs</Link>}>
        {runs === undefined && <EmptyLine>Loading…</EmptyLine>}
        {runs === null && <EmptyLine>The recent alerts couldn't be loaded.</EmptyLine>}
        {runs && runs.length === 0 && <EmptyLine>Nothing sent yet. Send a test to check it.</EmptyLine>}
        {runs && runs.length > 0 && <LinkRows label="Deliveries" items={runs.map(r => runItem(r, targetHref(t.id)))} />}
      </ArSection>

      {saveBar}
      {actions.dialogs}
      {canEdit && <AddToRouteDialog open={adding} target={t} routes={routes} onClose={() => setAdding(false)} onAdded={added} />}
    </AlertWorkspace>
  )
}

// A set secret can be replaced, never read: this draws only the replace
// input and button.
const SecretReplace = ({ field, busy, onSave }) => {
  const [v, setV] = useState('')
  const id = `ar-replace-${field.key}`
  return (
    <div className="ar-secret-replace">
      <label className="visually-hidden" htmlFor={id}>New {field.label}</label>
      <input id={id} className="form-control form-control-sm" type="password" autoComplete="new-password" placeholder="New value"
        value={v} onChange={e => setV(e.target.value)} />
      <button type="button" className="btn sw-btn btn-sm" disabled={!v || busy} onClick={() => { onSave(v); setV('') }}>Replace</button>
    </div>
  )
}
